import { useState, useRef, useEffect, useMemo, useContext } from 'react'
import ControlCheckbox from './ControlCheckbox'
import { ColumnFilterMatchModeContext } from './columnFilterMatchModeContext'
import {
  COLUMN_MATCH_MODES,
  MATCH_CONTAINS,
  buildColumnFilterValue,
  columnFilterMode,
  columnFilterPlaceholder,
} from './columnFilterMatchMode'
/* spec 117 / B6 — the stroked chevron, replacing a filled ▾. Reaching into
   reports/ from common/ is the precedent DataGrid.jsx:31 already set for this
   same icon module; queueIcons.jsx holds every hand-inlined glyph in the app
   and has no dependency of its own, so there is nothing circular about it. */
import { ChevronDownIcon } from '@components/reports/queue/queueIcons'

/**
 * Per-column header filter widget (CC-49317), shared by the flat (DataGrid) and
 * grouped (ReportGroupedGrid) report grids so the two can't drift (we were bitten
 * by a flat/grouped divergence in slice 2). Lives in common/ because the shared
 * DataGrid uses it; reports/ may depend on common/, not the reverse.
 *
 * Always renders a forgiving free-text input. When `pickerValues` is non-empty it
 * ALSO renders a multi-select value picker — searchable and capped so a
 * high-cardinality column (e.g. an 800-name Client column) stays usable.
 *
 * Emitted value (via onChange):
 *   - text-only mode (no pickerValues): a bare string, or `undefined` when empty.
 *   - picker mode: `{ text, picked }`, or `undefined` when fully cleared.
 * Callers map `undefined` to whatever "no filter" means in their store.
 *
 * Spec 171 — MATCH MODE, opt-in. With `matchModes` (or a
 * ColumnFilterMatchModeContext provider above it — how the flat report grid opts
 * in, because DataGrid renders this component with four fixed props) a native
 * <select> offers Contains / Starts with / Ends with for the typed text. Contains
 * still emits exactly the two shapes above; an anchored mode adds `mode` to an
 * object value (`{ text, mode }` / `{ text, picked, mode }`) and still emits
 * `undefined` when there is nothing to match. See columnFilterMatchMode.js.
 * Without the opt-in nothing here changes: no <select>, the same placeholder and
 * the same emitted values — which is what DataGrid's 12 non-report callers need,
 * because TanStack's `includesString` reads an object as "[object Object]".
 */

/*
 * A3 (Himaad, 2026-08-26) — ONE constant, and it IS the per-column filter field
 * on 136 of the 140 reports plus the 11 non-report list pages, because DataGrid
 * defaults `enableFiltering` to true. It was wrong on all three axes Himaad
 * named AND a live WCAG 2.1 SC 1.4.11 failure:
 *
 *   was: px-2 py-1 text-xs      -> 26px tall, out of step with every other control
 *        rounded                -> 4px, against the 6px --radius-rc-control
 *        border-gray-200        -> #e5e7eb, 1.24:1 on white. A white-on-white
 *                                  input's border is the ONLY thing identifying
 *                                  it as a control; SC 1.4.11 wants 3.0:1.
 *        focus:ring-1 cb-accent -> a 1px #479eff ring at 30% alpha, i.e. barely
 *                                  visible, and unlike every other rc control.
 *   now: .input-rc              -> 30px, 6px radius, --color-rc-control (3.03:1),
 *                                  and the same 2px chip-text focus outline the
 *                                  buttons and selects use.
 *
 * `.input-rc` is an UNLAYERED rule and Tailwind emits utilities inside
 * `@layer utilities`, so an unlayered rule beats any layered one regardless of
 * specificity: the old `px-2 py-1 text-xs rounded border-gray-200` utilities
 * would have been DEAD CODE next to it, not a fallback. They are deleted rather
 * than paired. Only `w-full` survives, because .input-rc deliberately sets no
 * width so call sites can size it.
 */
const INPUT_CLASS = 'w-full input-rc'
const MAX_VISIBLE = 200 // cap rendered checkboxes; user narrows the rest via search

export default function ColumnValuePicker({ label, value, pickerValues, onChange, matchModes, matchModeResetKey }) {
  // Spec 171: the prop wins; otherwise a report grid's provider; otherwise off.
  const modeCtx = useContext(ColumnFilterMatchModeContext)
  const modesOn = matchModes ?? !!modeCtx?.enabled
  const resetKey = matchModeResetKey ?? modeCtx?.resetKey
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [pos, setPos] = useState(null) // fixed-position coords for the popover (bug #1)
  const ref = useRef(null)
  const btnRef = useRef(null)

  // Open with the popover positioned via `position: fixed` anchored to the
  // caret button (computed from its rect, clamped to the viewport). It said
  // "the ▾ button" until 2026-09-09, an element the same change deleted — and a
  // glyph grep looking for the last unswept carets hit this prose and read as a
  // survivor. This escapes the
  // grid's overflow-x scroll container, which otherwise clips the popover —
  // worse as the grid shrinks after filtering.
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect()
    if (r) {
      const width = 240
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
      setPos({ top: Math.round(r.bottom + 4), left: Math.round(left), width })
    }
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    // Bug #2: close on OUTSIDE scrolls only — the capture-phase listener also
    // fires for scrolls inside the popover's own list, which closed the picker
    // the moment users scrolled a long value list (e.g. timekeepers).
    const onScroll = (e) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return
      setOpen(false) // a fixed popover would detach on outside scroll
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  const hasPicker = Array.isArray(pickerValues) && pickerValues.length > 0
  const text = (value && typeof value === 'object' ? value.text : (typeof value === 'string' ? value : '')) || ''
  const picked = (value && typeof value === 'object' && Array.isArray(value.picked)) ? value.picked : []

  // Search filter over the picker's values (cheap; only meaningful when open).
  const filtered = useMemo(() => {
    if (!hasPicker) return []
    const q = search.trim().toLowerCase()
    return q ? pickerValues.filter((v) => v.toLowerCase().includes(q)) : pickerValues
  }, [hasPicker, pickerValues, search])

  /*
   * Spec 171 — which operator is showing.
   *
   * While the column HAS a value, the value is the truth: its own `mode`, or
   * Contains for a bare string / a value with none (a legacy saved view). With no
   * value there is nothing to carry a mode — empty text is no filter, so the
   * picker emits `undefined` — and the operator the user picked lives here, so
   * choosing "Starts with" BEFORE typing still applies to what is typed next.
   *
   * It is remembered against the host's `resetKey`: when "Reset grid layout"
   * clears every filter the key changes, and a choice made under the old key
   * reads as Contains. Derived during render — no effect, no ref — so the reset
   * cannot be one paint late.
   */
  const [chosen, setChosen] = useState(() => ({ key: resetKey, mode: columnFilterMode(value) }))
  const hasValue = value != null && value !== ''
  const mode = !modesOn
    ? MATCH_CONTAINS
    : hasValue
      ? columnFilterMode(value)
      : (chosen.key === resetKey ? chosen.mode : MATCH_CONTAINS)
  const placeholder = columnFilterPlaceholder(mode)
  // One emitter for both column kinds. Under Contains it reproduces the two
  // pre-spec-171 emissions exactly (buildColumnFilterValue's note).
  const emit = (nextText, nextPicked) =>
    onChange(buildColumnFilterValue({ text: nextText, picked: nextPicked, mode, hasPicker }))

  /*
   * The operator control. A NATIVE <select>: keyboard-operable by construction
   * (Tab to it, arrows / type-ahead to choose, no custom key handling to get
   * wrong) and announced as "<column> match, Starts with" with its options.
   *
   * Stacked ABOVE the input rather than beside it, because a report column is
   * typically ~150px with 16px of cell padding a side, which leaves no room for
   * "Starts with" plus a usable input on one line — and the picker's chevron is
   * absolutely positioned inside the input's own box, so the input keeps its full
   * width and that button is untouched.
   *
   * `.select-rc` for the rc chevron, font and focus outline, with four `!`
   * overrides because `.select-rc` is UNLAYERED in src/index.css and would
   * defeat the plain utilities (CLAUDE.md, "the `!` modifier IS the escape
   * hatch"):
   *   h-6!          24px, not the 38px toolbar height, inside a dense header row
   *   pl-2!         8px, not 13px, for a narrow column
   *   text-[12px]!  one step under the input's 13px; the label is the quieter half
   *   border-rc-seam!  SC 1.4.11. Every filter cell sits on the header's gray-50,
   *                 #f9fafb (DataGrid's sticky thead, the grouped grid's thead and
   *                 the pinned `thead .cb-pin-cell` alike), and .select-rc's own
   *                 --color-rc-control #8b95a5 is 2.896:1 there — under the 3.0
   *                 floor. --color-rc-seam #6b7280 is 4.626:1 on #f9fafb and
   *                 4.834:1 on the select's own white fill (computed, spec 171 §5).
   * The label text is .select-rc's --color-rc-ink-2 #374151 on white, 10.31:1.
   */
  const modeSelect = modesOn ? (
    <select
      value={mode}
      onChange={(e) => {
        const next = e.target.value
        setChosen({ key: resetKey, mode: next })
        // Re-shape a live filter under the new operator. With nothing typed or
        // picked there is nothing to re-shape, and nothing is emitted.
        if (hasValue) onChange(buildColumnFilterValue({ text, picked, mode: next, hasPicker }))
      }}
      aria-label={`${label} match`}
      className="select-rc w-full h-6! pl-2! text-[12px]! border-rc-seam!"
    >
      {COLUMN_MATCH_MODES.map((m) => (
        <option key={m.value} value={m.value}>{m.label}</option>
      ))}
    </select>
  ) : null

  // Text-only column: plain forgiving free-text input.
  if (!hasPicker) {
    const input = (
      <input
        type="text"
        value={text}
        onChange={(e) => emit(e.target.value, picked)}
        placeholder={placeholder}
        aria-label={`Filter ${label}`}
        className={INPUT_CLASS}
      />
    )
    if (!modesOn) return input
    return (
      <div className="flex flex-col gap-1">
        {modeSelect}
        {input}
      </div>
    )
  }

  const togglePick = (val) =>
    emit(text, picked.includes(val) ? picked.filter((p) => p !== val) : [...picked, val])

  const shown = filtered.slice(0, MAX_VISIBLE)
  const overflow = filtered.length - shown.length

  /*
   * A3 round 2 (Jordan, 2026-08-26) — the chevron belongs INSIDE the box.
   *
   * Round 1 read the complaint as "the input and its chevron are two different
   * heights" and matched them both to 30px. That was not it. Himaad wanted ONE
   * control — a combobox whose dropdown affordance sits inside the field — not
   * two controls side by side, however well aligned. So the button is absolutely
   * positioned within the input's box instead of being its flex sibling, and the
   * wrapper drops `gap-1` because there is no longer a gap to set.
   *
   * The right padding is an INLINE STYLE deliberately. `.input-rc` sets its
   * padding UNLAYERED, and an unlayered rule beats any Tailwind utility
   * regardless of specificity — so a `pr-8` here would be dead code and the typed
   * text would run under the chevron. ReportFilterPanel documents this exact trap
   * for three other affix inputs and works around it by not using `.input-rc` at
   * all. An inline style beats both, and the value has to be computed anyway
   * because the affix is wider whenever a pick count is showing.
   */
  const affixPx = picked.length > 0 ? 44 : 28

  const pickerField = (
    <div ref={ref} className="relative flex items-center">
      <input
        type="text"
        value={text}
        onChange={(e) => emit(e.target.value, picked)}
        placeholder={placeholder}
        aria-label={`Filter ${label}`}
        className={INPUT_CLASS}
        style={{ paddingRight: `${affixPx}px` }}
      />
      <button
        ref={btnRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openMenu())}
        aria-label={`Pick ${label} values`}
        aria-haspopup="true"
        aria-expanded={open}
        /*
         * Inside the field, so it carries NO border and no fill of its own: the
         * input's own 3.03:1 border is the boundary, and a second one here would
         * rebuild the two-controls look this change exists to remove. It still
         * spans the full 30px height, so the hit target did not shrink.
         *
         * The picked count stays, because it is the only sign the column is
         * filtered by something the text box does not show. It takes the chip
         * TEXT colour (6.70:1 on white) rather than the chip fill — a tinted
         * pill in here would be a box inside a box again.
         */
        className={`absolute right-0 top-0 inline-flex h-[30px] items-center justify-center gap-1 rounded-r-rc-control px-2 text-[11px] leading-none ${picked.length > 0 ? 'text-rc-chip-text font-semibold' : 'text-rc-muted'} hover:text-rc-ink`}
      >
        {picked.length > 0 ? <span>{picked.length}</span> : null}
        {/* B6 (Himaad, spec 117) — an OUTLINE chevron, not the filled ▾ this was.
            "The empty one is more closer and aligned with our current pattern",
            and the pattern is `.select-rc`'s own chevron: the identical
            `M6 9l6 6 6-6` path in a 24 viewBox, which index.css:573 draws as a
            data-URI at `background-size: 14px 14px`. `h-3.5 w-3.5` is that same
            14px box, so the two render a geometrically IDENTICAL 7.0 x 3.5px mark
            (measured) and never disagree on size when both sit on one bar.

            It also keeps Jordan's 2026-08-26 note satisfied — the glyph stays
            sized independently of the button's 11px text, because at 11px it was
            hard to see and it is the only thing telling you the field has a value
            list behind it. The COUNT stays at 11px because it is a number that
            has to fit beside the chevron in a 44px affix.

            ARITHMETIC, corrected 2026-09-08 — an earlier version of this note
            claimed the 14px glyph fits "without widening the affix past the
            input's reserved padding", and that is false by 2px: unpicked, the
            button is px-2 (8+8) + a 14px svg = 30px wide against `affixPx` 28,
            so its transparent left edge overlaps the input's text content box by
            2px. Nothing is clipped and the value is deliberately NOT changed:
            the chevron's INK starts 8px in from the button's right edge, i.e.
            20px clear of where typed text stops, and 28/44 are the measured
            values the browser check was run against. The only consequence is
            that a caret click in a 2px column opens the picker. */}
        <ChevronDownIcon className="h-3.5 w-3.5" />
      </button>
      {open && (
        /* Card outline, not a control border: rc-line (#e5e7eb) is the RIGHT
           token here and must NOT be swept to rc-control — nothing about this
           1px line is what identifies an operable thing. Stated in tokens so the
           next SC 1.4.11 pass can see the distinction was deliberate. Same hexes
           as the `gray-200` / `rounded-lg` it replaces; no visual change.
           NB: a bare block comment, NOT `{/* … *\/}` — this position is inside a
           parenthesised expression, not JSX children, and the braced form is a
           parse error here. */
        <div role="menu" style={{ position: 'fixed', top: pos?.top, left: pos?.left, width: pos?.width, zIndex: 50 }} className="bg-white border border-rc-line rounded-rc-card shadow-lg p-1">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {picked.length > 0 ? `${picked.length} selected` : 'All'}
            </span>
            {picked.length > 0 && (
              <button type="button" onClick={() => emit(text, [])} className="text-[10px] text-cb-accent-text hover:underline">
                Clear
              </button>
            )}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${pickerValues.length} values…`}
            aria-label={`Search ${label} values`}
            className={`${INPUT_CLASS} mb-1`}
          />
          <div className="max-h-52 overflow-auto">
            {shown.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-gray-500">No matches</div>
            ) : (
              shown.map((val) => (
                <label key={val} className="flex items-center gap-2 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 rounded cursor-pointer">
                  {/* B4 (spec 117): the drawn control, replacing a native input
                      whose `rounded border-gray-300 text-cb-accent-text
                      focus:ring-cb-accent` painted NOTHING — Tailwind v4's
                      preflight zeroes `border-width` on `*` and there was no bare
                      `border` width beside the colour, so the browser drew its own
                      default. Those four classes are deleted rather than carried,
                      exactly because they were inert. */}
                  <ControlCheckbox
                    checked={picked.includes(val)}
                    onChange={() => togglePick(val)}
                  />
                  <span className="truncate">{val}</span>
                </label>
              ))
            )}
            {overflow > 0 && (
              <div className="px-2 py-1.5 text-[10px] text-gray-500">+{overflow} more — refine your search</div>
            )}
          </div>
        </div>
      )}
    </div>
  )

  // Spec 171: the operator sits OUTSIDE the ref'd box, so the popover's
  // click-outside test and the chevron's position are exactly as they were.
  if (!modesOn) return pickerField
  return (
    <div className="flex flex-col gap-1">
      {modeSelect}
      {pickerField}
    </div>
  )
}
